/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { setClassToken } from '@amazon/vinyl-tsx'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { globalData } from '@amzn/amp-playback'
import { documentEvents } from '@/util/interaction'
import { setStageControlsVisible, STAGE_CONTROLS_HIDE_SECONDS } from '@/video/videoStage'

export const MOUSE_WAKE_DISTANCE_PX = 10
export const MOUSE_WAKE_WINDOW_MS = 200

const idle$ = data(false)
const immersive$ = globalData(() => data(false))

/** True while the app root is full screen and its chrome is hidden for inactivity. */
export const fullscreenChromeIdle$: ObservableValue<boolean> = idle$

/** Treats the app root like full screen for the idle hide; the first key press then only wakes the chrome. */
export function setImmersiveChrome(immersive: boolean): void {
    immersive$.value = immersive
}

let revealBoundRoot: (() => void) | null = null

/** Counts as user activity: shows the full-screen chrome and restarts its idle dwell. */
export function revealFullscreenChrome(): void {
    revealBoundRoot?.()
}

/**
 * Auto-hides the chrome while `root` is full screen and the user is idle. The
 * activity surface is the whole root (header, transport, chat) plus any key
 * press, so keyboard users can always bring the chrome back.
 */
export function bindFullscreenChromeIdle(root: HTMLElement): Unsubscribe {
    const { add, dispose } = createDisposer()
    const isFullscreen = () => document.fullscreenElement === root || immersive$.value
    let hideTimer: number | null = null
    let unbindActivity: Unsubscribe | null = null

    const clearHideTimer = () => {
        if (hideTimer != null) window.clearTimeout(hideTimer)
        hideTimer = null
    }
    const setChromeHidden = (hidden: boolean) => {
        setClassToken(root, 'idleHideChrome', hidden)
        // Drives the same controls-visible seam the video stage uses, so its
        // floating buttons fade in lock-step with the rest of the chrome.
        setStageControlsVisible(!hidden)
        idle$.value = hidden
    }
    const reveal = () => {
        setChromeHidden(false)
        clearHideTimer()
        hideTimer = window.setTimeout(() => {
            if (isFullscreen()) setChromeHidden(true)
        }, STAGE_CONTROLS_HIDE_SECONDS * 1000)
    }
    const bindActivity = (): Unsubscribe => {
        const activity = createDisposer()
        for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
            root.addEventListener(type, reveal)
            activity.add(() => root.removeEventListener(type, reveal))
        }
        let lastPoint: { x: number; y: number } | null = null
        let burstStart = 0
        let burstDistance = 0
        const onMouseMove = (event: MouseEvent) => {
            const point = { x: event.screenX + event.clientX, y: event.screenY + event.clientY }
            const previous = lastPoint
            lastPoint = point
            if (!previous || (previous.x === point.x && previous.y === point.y)) return
            const now = Date.now()
            if (now - burstStart > MOUSE_WAKE_WINDOW_MS) {
                burstStart = now
                burstDistance = 0
            }
            burstDistance += Math.hypot(point.x - previous.x, point.y - previous.y)
            if (burstDistance < MOUSE_WAKE_DISTANCE_PX) return
            burstDistance = 0
            reveal()
        }
        root.addEventListener('mousemove', onMouseMove)
        activity.add(() => root.removeEventListener('mousemove', onMouseMove))
        // Capture phase so a Tab un-hides the chrome before the browser moves focus.
        activity.add(
            documentEvents.on(
                'keydown',
                (event) => {
                    if (immersive$.value && idle$.value) {
                        event.preventDefault()
                        event.stopPropagation()
                    }
                    reveal()
                },
                { capture: true }
            )
        )
        return activity.dispose
    }
    const syncMode = () => {
        unbindActivity?.()
        unbindActivity = null
        clearHideTimer()
        setClassToken(root, 'immersiveChrome', immersive$.value)
        if (!isFullscreen()) {
            setChromeHidden(false)
        } else if (immersive$.value) {
            unbindActivity = bindActivity()
            reveal()
        } else {
            unbindActivity = bindActivity()
            // Enter clean: the user just interacted to go full screen.
            setChromeHidden(true)
        }
    }
    let wasImmersive = false
    const onImmersiveChange = () => {
        if (immersive$.value === wasImmersive) return
        wasImmersive = immersive$.value
        syncMode()
    }

    const revealIfFullscreen = () => {
        if (isFullscreen()) reveal()
    }
    revealBoundRoot = revealIfFullscreen
    add(documentEvents.on('fullscreenchange', syncMode))
    add(immersive$.onData(onImmersiveChange))
    add(() => {
        setClassToken(root, 'immersiveChrome', false)
        unbindActivity?.()
        clearHideTimer()
        idle$.value = false
        if (revealBoundRoot === revealIfFullscreen) revealBoundRoot = null
    })
    return dispose
}
